
import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ExecutiveInquirySection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams(formData as any);

    try {
      const response = await fetch('https://readdy.ai/api/form/d69dkqlbiueuoqm1rsug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (!response.ok) throw new Error('Submit failed');
      setFormStatus('success');
      form.reset();
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="inquiry" className="relative bg-[#0A0E27] py-32">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase">
              Executive Inquiry
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 leading-tight">
              ビルボード投資パートナーシップ
              <br />
              エグゼクティブ相談
            </h2>
            <p className="text-white/60 text-lg mt-6 leading-relaxed">
              ラスベガスの屋外広告メディア資産への投資について、
              専任チームが48時間以内に戦略提案書を作成。
              貴社の市場拡大目標に最適化したプランをご提示します。
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-phone-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">直通連絡先</p>
                  <p className="text-white/50 text-sm">専任担当者への直接アクセス</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-user-star-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">専任コンサルタント</p>
                  <p className="text-white/50 text-sm">業界経験10年以上のエキスパート</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-shield-check-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">機密保持契約</p>
                  <p className="text-white/50 text-sm">NDA締結後の詳細情報開示</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              id="executive-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-10 shadow-2xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">お名前 *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">役職 *</label>
                  <input
                    type="text"
                    name="position"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="代表取締役 / CMO"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">会社名 *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">メールアドレス *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="example@company.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2">年間マーケティング予算 *</label>
                <select
                  name="budget"
                  required
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors cursor-pointer bg-white"
                >
                  <option value="">選択してください</option>
                  <option value="under-50m">5,000万円未満</option>
                  <option value="50m-200m">5,000万円〜2億円</option>
                  <option value="200m-500m">2億円〜5億円</option>
                  <option value="over-500m">5億円以上</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-3">関心のある資産タイプ</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['デジタルビルボード', 'ハイウェイ広告', '空港周辺', 'ダウンタウン', 'コンベンション', 'スタジアム'].map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="asset_types"
                        value={type}
                        className="w-4 h-4 rounded border-gray-300 text-amber-400 focus:ring-amber-400 cursor-pointer"
                      />
                      <span className="text-gray-600 text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <label className="block text-gray-700 text-sm font-medium mb-2">戦略目標・課題</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  placeholder="貴社の市場拡大目標や現在の課題をお聞かせください"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-4 bg-amber-400 text-black text-base font-semibold rounded-lg hover:bg-amber-300 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </span>
                ) : (
                  'エグゼクティブ相談を申し込む'
                )}
              </button>

              {formStatus === 'success' && (
                <p className="text-green-600 text-sm text-center mt-4">
                  送信完了しました。48時間以内に専任チームよりご連絡いたします。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-600 text-sm text-center mt-4">
                  送信に失敗しました。もう一度お試しください。
                </p>
              )}

              <p className="text-gray-400 text-xs text-center mt-4">
                送信後48時間以内に専任チームよりご連絡いたします
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
